import { makeResource } from '@/lib/api/resources';
import { api, type QueryParams } from '@/lib/api/client';
import type { BlockSchemaRegistry, ContentItem, OrderEntry } from '@/lib/api/types';

/** One resource client per collection key, base `/admin/{collection}` (spec §9.3). */
export function contentResource(collection: string) {
  return makeResource<ContentItem>(`/admin/${collection}`);
}

/**
 * Collection `doc` JSON Schemas (GET /admin/collection-schemas). The backend owns the
 * canonical shape of each collection's rich `doc`; the content editor is generated from
 * the matching entry (industry/project/article/job) so the form, server validation, and
 * the public site never drift. Collections without a backend entry fall back to the static
 * registry in `@/config/collections`.
 */
export function collectionSchemas(): Promise<BlockSchemaRegistry> {
  return api.get<BlockSchemaRegistry>('/admin/collection-schemas');
}

/** The list endpoint's page-size ceiling. */
const MAX_PER_PAGE = 100;
/** A runaway guard: no TEPCO collection comes near this many records. */
const MAX_PAGES = 50;

/**
 * Every row of a collection matching `params`, walking the pages at the server's maximum
 * page size. Reorder needs the full set (the server takes only a complete permutation), and
 * the "Missing AR" filter narrows client-side across every page because the list endpoint
 * has no Arabic-completeness filter.
 */
export async function loadAll(
  collection: string,
  params: QueryParams = {},
): Promise<ContentItem[]> {
  const res = contentResource(collection);
  const out: ContentItem[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const r = await res.list({ ...params, page, per_page: MAX_PER_PAGE });
    out.push(...r.data);
    const totalPages = r.meta?.pagination?.total_pages ?? 1;
    if (page >= totalPages || r.data.length === 0) break;
  }
  return out;
}

/**
 * PUT /admin/{collection}/order — the new display order as a complete permutation of the
 * collection's live ids. A 409 means the set changed underneath (an item was added or
 * removed); the caller reloads and asks the editor to arrange it again.
 */
export function saveOrder(collection: string, ids: string[]): Promise<{ order: OrderEntry[] }> {
  return api.put<{ order: OrderEntry[] }>(`/admin/${collection}/order`, { ids });
}

/** PUT /admin/{collection}/order/{placement} — the order of the rows placed on one page. */
export function savePlacementOrder(
  collection: string,
  placement: string,
  ids: string[],
): Promise<{ order: OrderEntry[] }> {
  return api.put<{ order: OrderEntry[] }>(
    `/admin/${collection}/order/${encodeURIComponent(placement)}`,
    { ids },
  );
}
